import { isSameDay, parseISO, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { css, styled } from 'styled-components';

import type { ApiListingLocationType } from 'api/types/ApiListingLocationType';
import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { Icon } from 'components/Icon/Icon';
import { Tag } from 'components/Tag/Tag';
import { useI18n } from 'i18n/hooks/useI18n';
import { ListingLocationLabel } from 'modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from 'modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { colors } from 'theme/theme';
import type { EmploymentType } from 'utils/constants/general/employmentTypes';
import { shorterDate, shorterTime } from 'utils/date';

import {
  SearchHitInfoBoxListingLabelsHolder,
  SearchHitInfoBoxListingLabelsPill,
  SearchHitInfoBoxListingLabelsPillContainer,
} from './SearchHitInfoBoxListingLabels.styled';

type Props = {
  listingType: 'ORG' | 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT' | 'IMPORTED';
  locationType: ApiListingLocationType | null;
  location: string | null;
  jobTypes: EmploymentType[];
  salaryFormatted: string | null;
  timezone: string | null;
  startDateTime: ApiISODateTimeString | null;
  endDateTime: ApiISODateTimeString | null;
  pillItems: string[];
};

const TagRowContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-flow: row wrap;
  gap: 8px;

  &:empty {
    display: none;
  }
`;

const StyledTag = styled(Tag)<{ $thickLeftBorder?: boolean }>`
  ${({ $thickLeftBorder }) =>
    $thickLeftBorder &&
    css`
      border-left: 5px solid ${colors.elementGrey};
    `};
`;

const formatDate = (
  startDateTime?: ApiISODateTimeString | null,
  endDateTime?: ApiISODateTimeString | null,
  timezone?: string | null,
) => {
  // 1/12/2024 - 4/02/2025
  if (
    startDateTime &&
    endDateTime &&
    !isSameDay(endDateTime, startDateTime) &&
    startOfDay(startDateTime).getTime() === parseISO(startDateTime).getTime() &&
    startOfDay(endDateTime).getTime() === parseISO(endDateTime).getTime()
  ) {
    return `${shorterDate(startDateTime)} - ${shorterDate(endDateTime)}`;
  }

  // 1/12/2024 10:00 AM - 5:00 PM EDT
  if (startDateTime && endDateTime && isSameDay(endDateTime, startDateTime)) {
    return `${shorterDate(startDateTime)} ${shorterTime(startDateTime, timezone, false)} - ${shorterTime(endDateTime, timezone, true)}`;
  }

  // 1/12/2024 10:00 AM - 4/02/2025 5:00 PM EDT
  if (startDateTime && endDateTime) {
    return `${shorterDate(startDateTime)} ${shorterTime(startDateTime, timezone, false)} - ${shorterDate(endDateTime)} ${shorterTime(endDateTime, timezone, true)}`;
  }

  // 1/12/2024 10:00 AM EDT
  if (startDateTime && !endDateTime) {
    return `${shorterDate(startDateTime)} ${shorterTime(startDateTime, timezone, true)}`;
  }
};

export function SearchHitInfoBoxListingLabels({
  listingType,
  location,
  jobTypes,
  locationType,
  salaryFormatted,
  timezone,
  startDateTime,
  endDateTime,
  pillItems,
}: Props) {
  const { i18n } = useI18n();

  const jobTypeLabel = useMemo(() => {
    // Note: order is important here.
    if (jobTypes.includes('CONTRACT')) return i18n('Contract/Freelance');
    if (jobTypes.includes('FULL_TIME')) return i18n('Full Time');
    if (jobTypes.includes('PART_TIME')) return i18n('Part Time');
  }, [i18n, jobTypes]);

  return (
    <SearchHitInfoBoxListingLabelsHolder>
      <TagRowContainer>
        {locationType && (
          <ListingLocationTypeLabel
            listingType={listingType}
            locationType={locationType}
          />
        )}

        {location &&
          locationType &&
          (listingType !== 'EVENT' ||
            (listingType === 'EVENT' && locationType !== 'REMOTE')) && (
            <ListingLocationLabel
              locationType={locationType}
              location={location}
            />
          )}
      </TagRowContainer>

      <TagRowContainer>
        {listingType === 'JOB' && (
          <StyledTag size="micro" $thickLeftBorder>
            {jobTypeLabel}
          </StyledTag>
        )}

        {salaryFormatted && (
          <StyledTag size="micro" $thickLeftBorder>
            {salaryFormatted}
          </StyledTag>
        )}

        {(startDateTime ?? endDateTime) && (
          <StyledTag size="micro" prefix={<Icon name="calendar" size={14} />}>
            {formatDate(startDateTime, endDateTime, timezone)}
          </StyledTag>
        )}
        {pillItems.length > 0 && (
          <SearchHitInfoBoxListingLabelsPillContainer>
            {pillItems.map((pillItem) => (
              <SearchHitInfoBoxListingLabelsPill key={pillItem}>
                {pillItem}
              </SearchHitInfoBoxListingLabelsPill>
            ))}
          </SearchHitInfoBoxListingLabelsPillContainer>
        )}
      </TagRowContainer>
    </SearchHitInfoBoxListingLabelsHolder>
  );
}
